import { InsightsMonthData } from '../../interfaces/insights.interface'

// Temporary cost-containment workaround: change this single value to adjust the standard insights cache window.
export const STANDARD_INSIGHTS_CACHE_TTL_MINUTES = 10

const CACHE_TTL_MS = STANDARD_INSIGHTS_CACHE_TTL_MINUTES * 60 * 1000
const CACHE_KEY_PREFIX = 'zenbox.insights-standard-cache:v1:'

export type InsightsCacheOptions = {
  enabled?: boolean;
  userId?: string;
  scope?: string;
}

type InsightsCacheEntry = {
  cachedAtUtc: string;
  data: InsightsMonthData[];
}

const sortForSerialization = (value: any): any => {
  if (Array.isArray(value)) return value.map(sortForSerialization)
  if (value && typeof value === 'object') {
    return Object.keys(value).sort().reduce((result, key) => {
      result[key] = sortForSerialization(value[key])
      return result
    }, {} as Record<string, any>)
  }
  return value
}

const serialize = (value: any) => JSON.stringify(sortForSerialization(value))

export const buildInsightsStandardCacheKey = ({
  userId,
  companyId,
  month,
  year,
  monthsRange,
  scope,
  request
}: {
  userId: string;
  companyId: any;
  month: string;
  year: string;
  monthsRange: number;
  scope: string;
  request: any;
}) => `${CACHE_KEY_PREFIX}${encodeURIComponent(serialize({ userId, companyId, month, year, monthsRange, scope, request }))}`

const getStorage = () => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch (_) {
    return null
  }
}

export const clearInsightsStandardCache = () => {
  const storage = getStorage()
  if (!storage) return
  try {
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => {
      try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
    })
  } catch (_) { /* cache is best effort */ }
}

export const readInsightsStandardCache = (key: string): InsightsMonthData[] | null => {
  const storage = getStorage()
  if (!storage) return null

  try {
    const raw = storage.getItem(key)
    if (!raw) return null
    const entry = JSON.parse(raw) as InsightsCacheEntry
    const cachedAt = Date.parse(entry?.cachedAtUtc)
    if (!Number.isFinite(cachedAt) || !Array.isArray(entry?.data)) {
      try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
      return null
    }
    if (Date.now() - cachedAt < CACHE_TTL_MS) return entry.data
    try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
  } catch (_) {
    try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
  }

  return null
}

const removeExpiredEntries = (storage: Storage) => {
  try {
    const now = Date.now()
    const keys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (key?.startsWith(CACHE_KEY_PREFIX)) keys.push(key)
    }
    keys.forEach((key) => {
      try {
        const entry = JSON.parse(storage.getItem(key) || '') as InsightsCacheEntry
        if (!Number.isFinite(Date.parse(entry?.cachedAtUtc)) || now - Date.parse(entry.cachedAtUtc) >= CACHE_TTL_MS) {
          storage.removeItem(key)
        }
      } catch (_) {
        try { storage.removeItem(key) } catch (_) { /* cache is best effort */ }
      }
    })
  } catch (_) { /* cache is best effort */ }
}

export const writeInsightsStandardCache = (key: string, data: InsightsMonthData[]) => {
  const storage = getStorage()
  if (!storage) return
  const entry: InsightsCacheEntry = { cachedAtUtc: new Date().toISOString(), data }
  try {
    removeExpiredEntries(storage)
    storage.setItem(key, JSON.stringify(entry))
  } catch (_) { /* cache is best effort; the backend response remains usable */ }
}
